import * as Sentry from '@sentry/react';
import type { UIMessage, UIMessageChunk } from 'ai';
import type { SyncTransport, TransportStatus } from '@/lib/sync/sync-transport';
import type { AssistantTransport } from './assistant-transport';
import { reconcileMessages } from './reconcile-messages';
import { useConversationRegistryStore } from '../stores/use-conversation-registry-store';

export interface ReconnectCoordinatorDeps {
  /**
   * The AUTHORITATIVE running set — from the connection-scoped ConversationRegistry
   * (use-conversation-registry-store, single liveness owner), keyed by conversationId,
   * holding per-conversation status derived from the WS firehose. The hook wires this
   * to the registry store's `running` statuses. NOT the transport's routing map.
   */
  getRunningConversations: () => Array<{ chatId: string; conversationId: string }>;
  /** The transport owns only the resume path (re-open the per-turn firehose stream). */
  transport: Pick<AssistantTransport, 'resumeRunIfLive'>;
  /** SyncTransport.onStatus — subscribe to connection status edges. Returns an unsubscribe. */
  onStatus: (handler: (status: TransportStatus) => void) => () => void;
  /** fetchConversationMessages — server-authoritative persisted transcript. */
  fetchMessages: (conversationId: string) => Promise<UIMessage[]>;
  /** Read the Chat's current live messages for a chat (the SDK owns these — no parallel buffer). */
  getLiveMessages: (chatId: string) => UIMessage[];
  /** Apply the reconciled list back to the Chat, plus the resumed stream (or null if run finished). */
  applyReconciled: (
    chatId: string,
    merged: UIMessage[],
    resumedStream: ReadableStream<UIMessageChunk> | null,
  ) => void;
}

/**
 * Reconnect-mid-run reconciliation (design §8.5). Subscribes to the shared
 * SyncTransport status. On a disconnected→connected EDGE (a true recovery, not
 * the first connect), for every conversation the REGISTRY (single liveness owner)
 * marks `running`, refetches the persisted transcript, reconciles it with the live
 * Chat messages deduped by stable id, and resumes the firehose if the run is
 * still live (else reconciles to the persisted, completed transcript).
 *
 * Returns a dispose function that unsubscribes from status.
 */
export function startReconnectCoordinator(deps: ReconnectCoordinatorDeps): () => void {
  let prev: TransportStatus | null = null;

  const unsubscribe = deps.onStatus((status) => {
    const wasDisconnected = prev === 'disconnected';
    prev = status;
    if (status !== 'connected' || !wasDisconnected) return;

    // Recovery edge: reconcile every conversation the REGISTRY marks running
    // (single liveness owner — not the transport's routing map).
    for (const { chatId, conversationId } of deps.getRunningConversations()) {
      void reconcileOne(deps, chatId, conversationId);
    }
  });

  return unsubscribe;
}

async function reconcileOne(
  deps: ReconnectCoordinatorDeps,
  chatId: string,
  conversationId: string,
): Promise<void> {
  let persisted: UIMessage[];
  try {
    persisted = await deps.fetchMessages(conversationId);
  } catch (err) {
    // A failed refetch on reconnect is an unexpected condition — the run may be
    // left unreconciled until the next edge. Capture so we see it in Sentry.
    console.error('[reconnectCoordinator] persisted refetch failed', { conversationId, err });
    Sentry.captureException(err, {
      extra: { context: 'reconnectCoordinator.refetch', conversationId, chatId },
    });
    return;
  }
  const merged = reconcileMessages(deps.getLiveMessages(chatId), persisted);
  // The run may complete DURING the await above (the persisted refetch races a
  // late DONE frame). That is benign: resumeRunIfLive re-checks runByChatId at
  // call time, so a run that finished mid-fetch returns resumed=false and we
  // reconcile to the freshly-fetched (now done) transcript — no stream re-opens.
  const { resumed, stream } = deps.transport.resumeRunIfLive(chatId);
  deps.applyReconciled(chatId, merged, resumed ? (stream ?? null) : null);
}

/**
 * The minimal shape the reconnect deps need from a registry-held bundle: a live
 * Chat whose `messages` can be read AND set, plus the chat's OWN transport. The
 * provider's ChatBundle is a structural superset of this.
 */
interface ResolvableBundle {
  chat: { messages: UIMessage[] };
  transport: Pick<AssistantTransport, 'resumeRunIfLive'>;
  // The registry's WarmChat constraint requires dispose; the provider's
  // ChatBundle supplies it. Unused here — we only read chat/transport.
  dispose: () => void;
}

/**
 * Build the production ReconnectCoordinatorDeps, resolving every per-chat
 * dependency against the connection-scoped ConversationRegistry (the single
 * liveness owner). Resolving by chatId is what guarantees resumeRunIfLive runs
 * on the RIGHT transport — each warm bundle owns its own transport, so we never
 * resume one chat's run on another chat's transport.
 *
 * The SDK's own reconnectToStream re-attaches the resumed stream into useChat;
 * resumeRunIfLive is idempotent (one firehose subscription per live run), so the
 * coordinator's job is the persisted-tail MERGE only. applyReconciled therefore
 * sets messages and ignores the resumedStream argument — it must NOT pump the
 * stream itself or it would race the SDK's attachment on the SAME shared stream.
 */
export function buildReconnectDeps(args: {
  syncTransport: SyncTransport;
  fetchMessages: (conversationId: string) => Promise<UIMessage[]>;
}): ReconnectCoordinatorDeps {
  const getBundle = (chatId: string): ResolvableBundle | null =>
    useConversationRegistryStore.getState().getWarmChat(chatId) as ResolvableBundle | null;

  return {
    getRunningConversations: () =>
      useConversationRegistryStore.getState().runningConversations(),
    transport: {
      // Route to the warm bundle's OWN transport. A cold (un-warm) chat has no
      // live transport to resume on → resumed=false; the coordinator then
      // reconciles to the persisted transcript (which hydrates on next open).
      resumeRunIfLive: (chatId: string) =>
        getBundle(chatId)?.transport.resumeRunIfLive(chatId) ?? { resumed: false },
    },
    onStatus: (handler) => args.syncTransport.onStatus(handler),
    fetchMessages: args.fetchMessages,
    getLiveMessages: (chatId) => getBundle(chatId)?.chat.messages ?? [],
    applyReconciled: (chatId, merged) => {
      const bundle = getBundle(chatId);
      // No warm bundle → no Chat to set; the persisted transcript hydrates on
      // next open (getLiveMessages returned [] so `merged` is just persisted).
      if (!bundle) return;
      // AbstractChat.messages = setter — same API loadConversation uses. The SDK
      // owns stream re-attach; we own the merge, so the stream arg is unused.
      bundle.chat.messages = merged;
    },
  };
}
